import { ReactNode, useCallback, useEffect, useState } from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import {
  CustomStylesType,
  TemplateProvider,
} from '@hhgtech/hhg-components/mantine'
import { useMantineTheme } from '@mantine/core'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { formatAirPopulationDataResult } from 'api/dataTransform/airPopulation'
import { PATHS } from 'api/paths'
import {
  AirPopulationCardInfo,
  AirPopulationItemResult,
} from 'interfaces/types/AirPopulation'
import { useAirPopulationStore } from 'state/airPopulation'
import { checkValidData, isValidStatus } from 'state/airPopulation/helpers'
import { getLocation } from 'utils/getCurrentLocation'

import { Body } from './Body'
import {
  AIR_POPULATION_CONFIG,
  CURRENT_LOCATION_KEY,
  CURRENT_LOCATION_LABEL_TRANSLATE_KEY,
} from './constants'
import { AirPopulationContext } from './context'
import {
  ChartSizeType,
  AirPopulationStatusItem,
  GetResultParamType,
} from './index.types'

export type AirPopulationProps = {
  children?:
    | ((param: {
        cardInfo: AirPopulationCardInfo | null
        result: AirPopulationItemResult | null
      }) => ReactNode)
    | ReactNode
  size?: ChartSizeType
  borderOutsideColor?: string
  backgroundChartColor?: string
  backgroundColor?: string
  selectorZIndex?: number
}

export const Container = ({
  children,
  size = 'md',
  borderOutsideColor,
  backgroundChartColor,
  backgroundColor = '#fff',
  selectorZIndex,
}: AirPopulationProps) => {
  const { formatMessage: f } = useIntl()
  const {
    cardInfo,
    currentLocation,
    setCurrentLocation,
    listProvinces,
    currentData,
    fetchInitData,
  } = useAirPopulationStore(
    (state) => ({
      cardInfo: state.cardInfo,
      listProvinces: state.listProvinces,
      currentData: state.currentData,
      currentLocation: state.currentLocation,
      fetchInitData: state.fetchInitData,
      setCurrentLocation: state.setCurrentLocation,
    }),
    shallow,
  )
  const [selectedValue, setSelectedValue] = useState<string | null>(null)
  const [data, setData] = useState<AirPopulationStatusItem | null>(null)
  const [firstLoading, setFirstLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [notLoadResultError, setNotLoadResultError] = useState<string | null>(
    null,
  )
  const [notShareLocationErr, setNotShareLocationErr] = useState(false)
  const theme = useMantineTheme()

  const handleClickCurrentLocation = useCallback(async () => {
    try {
      setLoading(true)
      if (currentLocation?.location) {
        handleFetchResult({
          type: 'geo',
          lat: String(currentLocation.location.latitude),
          lng: String(currentLocation.location.longitude),
        })
      } else {
        const res = await getLocation()
        if (res?.location) {
          handleFetchResult({
            type: 'geo',
            lat: String(res.location.latitude),
            lng: String(res.location.longitude),
          })
          setCurrentLocation(res)
        } else {
          setCurrentLocation(res)
          setNotShareLocationErr(true)
        }
      }
    } catch (error: any) {
    } finally {
      setLoading(false)
    }
  }, [])

  const handleFetchResult = async (params: GetResultParamType) => {
    try {
      setLoading(true)
      const res = await callApiNew(
        PATHS.HEALTH_TOOL.GET_AIR_POPULATION_SPECIFIC_PROVINCE,
        { params },
      )
      const formatedData = formatAirPopulationDataResult(res?.data)
      const isValidResult = checkValidData(formatedData, params.type === 'geo')
      if (
        isValidResult &&
        formatedData?.keyCategory &&
        isValidStatus(formatedData.keyCategory)
      ) {
        const currentState = AIR_POPULATION_CONFIG[formatedData?.keyCategory]
        setData({
          ...currentState,
          ...formatedData,
        })
        setNotLoadResultError(null)
      } else {
        setNotLoadResultError('')
      }
    } catch (err) {
      setNotLoadResultError('')
    } finally {
      setLoading(false)
      setNotShareLocationErr(false)
    }
  }

  const onChangeValue = (value: string) => {
    if (value === CURRENT_LOCATION_KEY) {
      handleClickCurrentLocation()
    } else {
      handleFetchResult({
        type: 'state',
        name: value,
      })
    }
  }

  useEffect(() => {
    fetchInitData()
  }, [fetchInitData])

  useEffect(() => {
    if (
      !!listProvinces?.length &&
      typeof currentData?.currentAqiusValue === 'number'
    ) {
      setFirstLoading(false)
    }
  }, [listProvinces, currentData])

  useEffect(() => {
    if (!currentData) return
    const currentState = AIR_POPULATION_CONFIG[currentData?.keyCategory]
    setData({
      ...currentState,
      ...currentData,
    })
    console.log({ currentLocation })
    setSelectedValue(
      currentLocation
        ? f({
            id: CURRENT_LOCATION_LABEL_TRANSLATE_KEY,
          })
        : currentData?.province,
    )
  }, [currentData])

  return (
    <TemplateProvider variant={CustomStylesType.LEADGEN}>
      <AirPopulationContext.Provider
        value={{
          notShareLocationErr,
          notLoadResultError,
          data,
          setData,
          loading,
          firstLoading,
          heading: 'Air polulation',
          disclaimer:
            'Information is for reference only and not a substitute for medical advice.',
          avatar: CommonGAssets.getAssetPath('tiki/maskcot.png'),
          size,
          borderOutsideColor: borderOutsideColor || theme.colors.neutral[0],
          backgroundChartColor: backgroundChartColor || '#fff',
          backgroundColor,
          selectorZIndex,
          selectedValue,
          setSelectedValue,
          onChangeValue,
          handleClickCurrentLocation,
        }}
      >
        {typeof children === 'function'
          ? children({ cardInfo, result: data })
          : children || <Body />}
      </AirPopulationContext.Provider>
    </TemplateProvider>
  )
}
